// Rewrite `mapbox://` URLs to their public HTTPS equivalents so MapLibre
// (which doesn't natively resolve the `mapbox://` scheme) can fetch sprites,
// glyphs, and vector tilesets.
//
// Wired in via `transformRequest` on `new maplibregl.Map(...)`. Anything
// that isn't a `mapbox://` URL is passed through unchanged.

import type { RequestParameters } from "maplibre-gl";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOXGL_TOKEN as string;

export function transformMapboxRequest(
  url: string,
  _resourceType?: string
): RequestParameters {
  if (!url.startsWith("mapbox://")) return { url };

  // mapbox://sprites/{user}/{styleId}/{sprite} → /styles/v1/{user}/{styleId}/sprite{sprite}
  if (url.startsWith("mapbox://sprites/")) {
    const rest = url.slice("mapbox://sprites/".length);
    const [user, styleId, ...spriteParts] = rest.split("/");
    const spriteSuffix = spriteParts.length ? `/${spriteParts.join("/")}` : "";
    return {
      url: `https://api.mapbox.com/styles/v1/${user}/${styleId}/sprite${spriteSuffix}?access_token=${MAPBOX_TOKEN}`,
    };
  }

  // mapbox://fonts/{user}/{fontstack}/{range}.pbf → /fonts/v1/{user}/{fontstack}/{range}.pbf
  if (url.startsWith("mapbox://fonts/")) {
    const rest = url.slice("mapbox://fonts/".length);
    return {
      url: `https://api.mapbox.com/fonts/v1/${rest}?access_token=${MAPBOX_TOKEN}`,
    };
  }

  // mapbox://{tileset_id}[,{tileset_id2},...] → composite TileJSON via /v4
  const tilesetIds = url.slice("mapbox://".length);
  return {
    url: `https://api.mapbox.com/v4/${tilesetIds}.json?secure&access_token=${MAPBOX_TOKEN}`,
  };
}
